import PropTypes from 'prop-types';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import VisuallyHiddenInput from 'components/VisuallyHiddenInput';
import Badge from 'components/Badge';
import Subtitle from 'components/Subtitle';
import Loader from 'components/SectionLoader';
import Button from 'components/Button';
import { useTranslation } from 'react-i18next';
import { isCpf } from 'utils/validation';
import { Avatar, Box, GridLegacy as Grid, TextField, Typography } from '@mui/material';
import Fade from 'components/Fade';
import styles from './styles.module.css';

const BADGE_STATUS = {
  ACTIVE: 'success',
  PENDING: 'pending',
  SUSPENDED: 'danger',
  DECLINED: 'alert',
  BLOCKED: 'blocked',
};

const showImagePreview = (file, callback) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onloadend = () => callback(reader.result);
};

function MemberPersonalInfoSection({ member, values, errors, onChange }) {
  const { photo, username, firstName, lastName, birthYear } = values;

  const { t } = useTranslation();

  const status = member.status || 'ACTIVE';

  const handleOnImageChange = (event) => {
    const file = event.target.files[0];

    if (!file) {
      return;
    }

    showImagePreview(file, (previewUrl) => {
      onChange({ photo: previewUrl, photoFile: file });
    });
  };

  return (
    <Box maxWidth="428px">
      <Subtitle>{t('organization.organization-members-edit.title-sub-member-edit')}</Subtitle>
      <Typography variant="body2" color="secondary" marginBottom="24px">
        {t('organization.organization-members-edit.text-organization-members-main')}
      </Typography>

      <Loader loading={!member.id}>
        <Fade>
          <Grid sx={{ margin: '0 0 40px 0' }} spacing="16px" container justifyContent="center" alignItems="center">
            <Grid container justifyContent="center" item xs={12} md={4}>
              <Avatar sx={{ width: 120, height: 120 }} src={photo} alt="profile image" />
            </Grid>

            <Grid container justifyContent="center" item xs={6} md={4}>
              <Button component="label" variant="outlined" startIcon={<CameraAltIcon />}>
                {t('braincare.organizations-edit.step-basic-info.button-add-photo')}
                <VisuallyHiddenInput onChange={handleOnImageChange} type="file" />
              </Button>
            </Grid>

            <Grid container justifyContent="center" item xs={6} md={4}>
              <Button
                variant="outlined"
                color="error"
                onClick={() => onChange({ photo: null, photoFile: '' })}
              >
                {t('braincare.organizations-edit.step-basic-info.button-remove-photo')}
              </Button>
            </Grid>
          </Grid>

          <div className={styles.statusContainer}>
            <div>
              <b>{t('organization.organization-members-edit.label-status')}</b>
              <Badge className={styles.statusBadge} type={BADGE_STATUS[status]} data-testid="status-badge">
                {t(`utils.status.${status.toLowerCase()}`)}
              </Badge>
            </div>
          </div>

          <TextField
            sx={{ margin: '16px 0 0 0' }}
            id="document"
            value={username}
            label={t('account.member-edit-personal-info.input-document')}
            variant="outlined"
            data-testid="document-text-field"
            InputProps={{
              readOnly: true,
              inputProps: {
                'data-testid': 'document-input',
                maxLength: isCpf(username) ? '14' : '254',
              },
            }}
            fullWidth
          />
          <TextField
            id="firstName"
            sx={{ margin: '32px 0 0 0' }}
            value={firstName}
            onChange={(event) => onChange({ firstName: event.target.value })}
            label={t('account.member-edit-personal-info.input-first-name')}
            variant="outlined"
            data-testid="first-name-text-field"
            error={errors.has('firstName')}
            helperText={errors.get('firstName')}
            FormHelperTextProps={{ 'data-testid': 'first-name-helper-text' }}
            inputProps={{ 'data-testid': 'first-name-input' }}
            fullWidth
          />
          <TextField
            id="lastName"
            sx={{ margin: '32px 0 0 0' }}
            value={lastName}
            onChange={(event) => onChange({ lastName: event.target.value })}
            label={t('account.member-edit-personal-info.input-last-name')}
            variant="outlined"
            data-testid="last-name-text-field"
            error={errors.has('lastName')}
            helperText={errors.get('lastName')}
            FormHelperTextProps={{ 'data-testid': 'last-name-helper-text' }}
            inputProps={{ 'data-testid': 'last-name-input' }}
            fullWidth
          />
          <TextField
            id="birthYear"
            sx={{ margin: '32px 0 32px 0' }}
            value={birthYear}
            onChange={(event) => onChange({ birthYear: event.target.value })}
            label={t('account.member-edit-personal-info.input-birth-year')}
            variant="outlined"
            data-testid="birth-year-text-field"
            error={errors.has('birthYear')}
            helperText={errors.get('birthYear')}
            inputProps={{ 'data-testid': 'birth-year-input' }}
            fullWidth
          />
        </Fade>
      </Loader>
    </Box>
  );
}

MemberPersonalInfoSection.propTypes = {
  member: PropTypes.shape({
    id: PropTypes.number,
    status: PropTypes.string,
  }),
  values: PropTypes.shape({
    photo: PropTypes.string,
    photoFile: PropTypes.oneOfType([PropTypes.instanceOf(File), PropTypes.string]),
    username: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    birthYear: PropTypes.string,
  }).isRequired,
  errors: PropTypes.instanceOf(Map).isRequired,
  onChange: PropTypes.func.isRequired,
};

MemberPersonalInfoSection.defaultProps = {
  member: {},
};

export default MemberPersonalInfoSection;
